import {Code} from 'lucide-react';
import {AppInformation} from './AppInformation';
import {CustomStatusEditor,StatusBubble} from './CustomStatus';
import {ProfileSecretTrigger} from './ProfileSecretTrigger';
import { GradientPicker } from './GradientPicker';
import { GuildTagPicker } from './GuildTagPicker';
import {SpotifyConnect} from './SpotifyConnect';
import {LinkConnections} from './Connections';
import {AccountDeletion} from './AccountDeletion';
import { KeybindSettings } from './KeybindSettings';
import {ChangePassword} from './ChangePassword';
import {TwoFactorSettings} from './TwoFactorSettings';
import {ProfileBio} from './ProfileBio';
import {useAnimatedValue} from './useAnimatedToggle';
import {ClipsSettings} from './Clips';
import {ProfileMediaPicker} from './ProfileMediaPicker';
import {MacDownloads} from './MacDownloads';
import {BadgeIcon} from './BadgeIcon';
import {BadgeToken} from './BadgeToken';
import {profileTheme} from './profileTheme';
import {IdentityBadges} from './IdentityBadges';
import {avatarSrc as defaultAvatarSrc,bannerFallback} from './avatar';
import { AudioSettings } from './AudioSettings';
import { SupportAdmin } from './SupportAdmin';
import { AccountSessions, PersonalData } from './AccountSessions';
import React, { useEffect, useState } from 'react';
import {
  User,
  Shield,
  Sparkles,
  LogOut,
  Upload,
  Eye,
  EyeOff,
  Check,
  Mail,
  Monitor,
  Download,
  Lock,
  Briefcase,
  Bug,
  MessageCircle,
  Mic,
  Bell,
  ChevronRight,
  AtSign,
  FileText,
  Paintbrush,
  Globe,
  Award,
  Palette
, Keyboard } from 'lucide-react';
import { Subscription } from './Subscription';
import {
  AVAILABLE_CUSTOM_BADGES,
  THEME_PRESETS,
  applyAppearanceSettings,
  getAccountAgeText,
  getUserBadges,
  useLang
} from './i18n';
import { ImageCropModal } from './ImageCropModal';
import { CustomSelect } from './CustomSelect';

const PROFILE_SWATCHES_PRIMARY = ['#ef5252', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#6366f1'];
const PROFILE_SWATCHES_SECONDARY = ['#262636', '#822b4b', '#1e3a8a', '#311042', '#064e3b', '#78350f', '#4c1d95', '#0f172a'];

type Profile = {
  id: string;
  username: string;
  displayName: string;
  bio: string;
  color: string;
  color2: string;
  status: string;
  customStatus?:string;customStatusUntil?:number|null;
  avatar: string | null;
  banner?: string | null;
  email?: string;
  verified?: number;
  twoFactorEnabled?: boolean;
  isOwnerAdmin?: boolean;
  teamAccess?: boolean;
  created?: number;
  customBadges?: string[];
  earlySupporterUnlocked?: boolean;
  earlySupporterCutoff?: number;
  profileGradient?: boolean;
  staffBadgeUnlocked?: boolean;
  hideStaffBadge?: boolean;
  developerBadgeUnlocked?: boolean;
  hideDeveloperBadge?: boolean;
  moderatorBadgeUnlocked?: boolean;
  hideModeratorBadge?: boolean;
};

type Props = {
  me: Profile;
  initialTab?: string;
  billingAvailable: boolean;
  request: (url: string, method?: string, body?: unknown) => Promise<any>;
  upload: (file: File) => Promise<{ id: string }>;
  onSaved: () => Promise<void>;
  onBlocked: () => void;
  onLogout: () => Promise<void>;
  onSwitchAccount?:()=>void;
};

export function SettingsPanel({
  me,
  initialTab = 'account',
  request,
  upload,
  onSaved,
  onLogout,
  onSwitchAccount,
  onBlocked
}: Props) {
  const [lang, setLang, tr] = useLang();
  const [tab, setTab] = useState(initialTab);
  const [changingPassword,setChangingPassword]=useState(false);
  const [statusDraft, setStatusDraft] = useState<string|undefined>();
  const [draft, setDraft] = useState<Profile>({
    ...me,
    customBadges: Array.isArray(me.customBadges) ? me.customBadges : [],
    profileGradient: !!me.profileGradient
  });
  const [busy, setBusy] = useState(false);
  const [badgeHint, setBadgeHint] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [showMail, setShowMail] = useState(false);
  const [editingMail, setEditingMail] = useState(false);
  const [mailDraft, setMailDraft] = useState(me.email || '');
  const [mailPassword, setMailPassword] = useState('');
  const [sessionCount, setSessionCount] = useState(1);
  const [mediaPicker,setMediaPicker,mediaPickerClosing]=useAnimatedValue<'avatar'|'banner'>();
  const [cropPending, setCropPending] = useState<{ file: File; kind: 'avatar' | 'banner' } | null>(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  // Appearance state
  const [density, setDensity] = useState(localStorage.getItem('malvo-density') || 'normal');
  const [showDiscovery,setShowDiscovery]=useState(localStorage.getItem('malvo-show-discovery')!=='0');
  const [chatPosition,setChatPosition]=useState(localStorage.getItem('malvo-chat-open-mode')==='last-position'?'last-position':'bottom');
  const [themePreset, setThemePreset] = useState(localStorage.getItem('malvo-theme') || 'midnight');
  const [accentColor, setAccentColor] = useState(localStorage.getItem('malvo-accent') || '#ef5252');
  const [btnStyle, setBtnStyle] = useState(localStorage.getItem('malvo-btn-style') || 'rounded');
  const [outgoingRing,setOutgoingRing]=useState(localStorage.getItem('malvo-outgoing-ring')!=='0');
  const [notifSound, setNotifSound] = useState(localStorage.getItem('malvo-notif-sound') !== '0');
  const [notifPopups,setNotifPopups]=useState(localStorage.getItem('malvo-notif-popups')!=='0');
  const [notifDesktop, setNotifDesktop] = useState(localStorage.getItem('malvo-notif-desktop') !== '0');
  const [notifAllServer, setNotifAllServer] = useState(localStorage.getItem('malvo-notif-all-server') === '1');
  const [shareAct, setShareAct] = useState(localStorage.getItem('malvo-share-activity') !== '0');
  useEffect(() => {
    const update = () => setShareAct(localStorage.getItem('malvo-share-activity') !== '0');
    window.addEventListener('malvo-activity-share', update);
    return () => window.removeEventListener('malvo-activity-share', update);
  }, []);

  useEffect(() => {
    setDraft(prev => ({
      ...prev,
      ...me,
      customBadges: Array.isArray(me.customBadges) ? me.customBadges : prev.customBadges || [],
      profileGradient: me.profileGradient !== undefined ? !!me.profileGradient : prev.profileGradient
    }));
  }, [me.id, me.avatar, me.banner, me.profileGradient, me.hideStaffBadge, me.hideDeveloperBadge, me.hideModeratorBadge, me.color, me.color2]);

  useEffect(() => {
    request('/account/sessions')
      .then((rows: any[]) => {
        if (Array.isArray(rows)) setSessionCount(rows.length);
      })
      .catch(() => {});
  }, []);

  async function action(fn: () => Promise<void>) {
    setBusy(true);
    setError('');
    setNote('');
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function picture(file: File, kind: 'avatar' | 'banner') {
    await action(async () => {
      const f = await upload(file);
      await request('/profile/' + kind, 'POST', { uploadId: f.id });
      setDraft(d => ({ ...d, [kind]: f.id }));
      await onSaved();
      setNote(tr('Image saved.', 'Bild gespeichert.'));
    });
  }

  const profileFields = (p: any) =>
    JSON.stringify([p.username, p.displayName, p.bio || '', p.color, p.color2, p.status, p.customBadges || [], !!p.profileGradient, !!p.hideStaffBadge, !!p.hideDeveloperBadge, !!p.hideModeratorBadge]);
  const profileDirty = profileFields(draft) !== profileFields({ ...me, customBadges: Array.isArray(me.customBadges) ? me.customBadges : [] });
  function resetProfileDraft() {
    setDraft({ ...me, customBadges: Array.isArray(me.customBadges) ? me.customBadges : [], profileGradient: !!me.profileGradient });
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    await action(async () => {
      const { username, displayName, bio, color, color2, status, customBadges, profileGradient, hideStaffBadge, hideDeveloperBadge, hideModeratorBadge } = draft;
      await request('/profile', 'PATCH', {
        username: username.trim().toLowerCase(),
        displayName,
        bio,
        color,
        color2,
        status,
        customBadges: customBadges || [],
        profileGradient: !!profileGradient,
        ...(me.staffBadgeUnlocked ? { hideStaffBadge: !!hideStaffBadge } : {}),
        ...(me.developerBadgeUnlocked ? { hideDeveloperBadge: !!hideDeveloperBadge } : {}),
        ...(me.moderatorBadgeUnlocked ? { hideModeratorBadge: !!hideModeratorBadge } : {})
      });
      await onSaved();
      setNote(tr('Profile saved.', 'Profil gespeichert.'));
    });
  }

  async function saveEmail(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const code=new FormData(e.currentTarget).get('code')||undefined;
    await action(async () => {
      await request('/account/email', 'PATCH', { email: mailDraft.trim(), password: mailPassword, code });
      setEditingMail(false);
      setMailPassword('');
      setNote(tr('Email updated. Please confirm the new address.', 'E-Mail-Adresse geändert. Bitte bestätige die neue Adresse.'));
      await onSaved();
    });
  }

  function toggleBadge(id: string) {
    const cur = draft.customBadges || [];
    if (cur.includes(id)) {
      setDraft({ ...draft, customBadges: cur.filter(x => x !== id) });
    } else if (cur.length < 6) {
      setDraft({ ...draft, customBadges: [...cur, id] });
    }
  }

  const tabTitleMap: Record<string, { label: string; Icon: any }> = {
    account: { label: tr('Sign-in and security', 'Anmeldung und Sicherheit'), Icon: Shield },
    sessions:{label:tr('Devices and sessions','Geräte und Sitzungen'),Icon:Monitor},
    connections:{label:tr('Connections','Verbindungen'),Icon:Globe},
    info:{label:tr('App information','Appinformationen'),Icon:FileText},
    profile: { label: tr('Edit Profile', 'Profil bearbeiten'), Icon: User },
    security: { label: tr('Security', 'Sicherheit'), Icon: Shield },
    privacy: { label: tr('Privacy & Safety', 'Privatsphäre'), Icon: Lock },
    data: { label: tr('My Data', 'Meine Daten'), Icon: Briefcase },
    subscription: { label: 'Beyond', Icon: Sparkles },
    beta: { label: tr('Closed Beta', 'Closed Beta'), Icon: Bug },
    chat: { label: tr('Chat', 'Chat'), Icon: MessageCircle },
    appearance: { label: tr('Appearance & Language', 'Design & Sprache'), Icon: Paintbrush },
    voice: { label: tr('Voice & Video', 'Sprache & Video'), Icon: Mic },
    notifications: { label: tr('Notifications', 'Benachrichtigungen'), Icon: Bell },
    developer: {label:tr('Developer Portal','Entwicklerportal'),Icon:Code},
    desktop: { label: /Android/i.test(navigator.userAgent)?tr('App & Updates','App & Updates'):tr('Desktop App', 'Desktop-App'), Icon: Monitor },
    support: { label: tr('Team Dashboard','Team-Dashboard'), Icon: Shield }
  };
  const activeHeader = tabTitleMap[tab] || tabTitleMap.account;
  const HeaderIcon = activeHeader.Icon;
  const avatarSrc = defaultAvatarSrc(draft);
  const previewBadges = getUserBadges(draft);

  return (
    <div className="settings-panel flackle-settings">
      {cropPending && (
        <ImageCropModal
          file={cropPending.file}
          aspect={cropPending.kind}
          onCancel={() => setCropPending(null)}
          onConfirm={cropped => {
            const k = cropPending.kind;
            setCropPending(null);
            void picture(cropped, k);
          }}
        />
      )}

      <header className="settings-heading">
        <HeaderIcon size={18} />
        <span>{tr('Settings','Einstellungen')}</span>{onSwitchAccount&&<button type="button" className="settings-switch-account" onClick={onSwitchAccount}>{tr('Switch account','Konto wechseln')}</button>}
      </header>
      <div className="settings-grid">
        <aside className="settings-navigation"><label className="settings-mobile-section">{tr('Section','Bereich')}<select data-native aria-label={tr('Settings section','Einstellungsbereich')} value={tab} onChange={e=>{if(e.target.value==='developer'){location.assign('/developer/');return;}setTab(e.target.value);setError('');setNote('')}}>{Object.entries(tabTitleMap).filter(([key])=>key!=='support').map(([key,value])=><option key={key} value={key}>{value.label}</option>)}</select></label>
          <button
            type="button"
            className={'settings-current-user ' + (tab === 'profile' ? 'active' : '')}
            onClick={() => {
              setTab('profile');
              setError('');
              setNote('');
            }}
          >
            <span
              className="settings-avatar"
              style={{ background: `linear-gradient(120deg,${draft.color},${draft.color2})` }}
            >
              {avatarSrc ? <img src={avatarSrc} alt={draft.displayName} /> : draft.displayName.slice(0, 2)}
            </span>
            <span className="settings-user-meta">
              <b>{draft.displayName}</b>
              <small>
                {tr('Edit Profile', 'Profil bearbeiten')} <ChevronRight size={12} />
              </small>
            </span>
          </button>
          <div className="settings-nav-card">
            <div className="settings-nav-scroll">
              <button type="button" className={'settings-mobile-profile '+(tab==='profile'?'active':'')} onClick={()=>{setTab('profile');setError('');setNote('')}}><User size={16}/>{tr('Profile','Profil')}</button>
              {[
                ['account', tr('Sign-in and security', 'Anmeldung und Sicherheit'), Shield],
                ['sessions',tr('Devices and sessions','Geräte und Sitzungen'),Monitor],
                ['connections',tr('Connections','Verbindungen'),Globe],
                ['info',tr('App information','Appinformationen'),FileText],
                ['privacy', tr('Privacy & Safety', 'Privatsphäre'), Lock],
                ['data', tr('My Data', 'Meine Daten'), Briefcase],
                ['beta', 'Closed Beta', Bug]
              ].map(([key, label, Icon]: any) => (
                <button
                  type="button"
                  key={key}
                  className={tab === key ? 'active' : ''}
                  onClick={() => {
                    setTab(key);
                    setError('');
                    setNote('');
                  }}
                >
                  <Icon size={16} />
                  {label}
                </button>
              ))}
              <div className="settings-nav-group">{tr('Chat & Theme', 'Chat & Design')}</div>
              {[
                ['appearance', tr('Appearance & Language', 'Design & Sprache'), Paintbrush],
                ['chat', tr('Chat', 'Chat'), MessageCircle]
              ].map(([key, label, Icon]: any) => (
                <button
                  type="button"
                  key={key}
                  className={tab === key ? 'active' : ''}
                  onClick={() => {
                    setTab(key);
                    setError('');
                    setNote('');
                  }}
                >
                  <Icon size={16} />
                  {label}
                </button>
              ))}
              <div className="settings-nav-group">{tr('App Settings', 'App-Einstellungen')}</div>
              {[
                ['voice', tr('Voice & Video', 'Sprache & Video'), Mic],
                ['clips','Clips',Monitor],
                ['notifications', tr('Notifications', 'Benachrichtigungen'), Bell],
                ['keybinds', tr('Keybinds', 'Tastenkürzel'), Keyboard],
                ['developer',tr('Developer Portal','Entwicklerportal'),Code],
                ['desktop', /Android/i.test(navigator.userAgent)?'App & Updates':tr('Desktop App', 'Desktop-App'), Monitor],
                ...(me.teamAccess ? [['support',tr('Team Dashboard','Team-Dashboard'),Shield]] : [])
              ].map(([key, label, Icon]: any) => (
                <button
                  type="button"
                  key={key}
                  className={tab === key ? 'active' : ''}
                  onClick={() => {
                    if(key==='developer'){location.assign('/developer/');return;}if(key==='support'){window.dispatchEvent(new Event('malvo-open-admin'));return;}
                    setTab(key);
                    setError('');
                    setNote('');
                  }}
                >
                  <Icon size={16} />
                  {label}
                </button>
              ))}
            </div>
            <div className="settings-nav-footer">
              <button type="button" title={tr('My Data', 'Meine Daten')} onClick={() => setTab('data')}>
                <FileText size={16} />
              </button>
              <button
                type="button"
                title={tr('Language: EN / DE', 'Sprache: DE / EN')}
                onClick={() => setLang(lang === 'en' ? 'de' : 'en')}
              >
                <Globe size={16} />
              </button>
              <button
                type="button"
                title={tr('Log Out', 'Abmelden')}
                disabled={busy}
                onClick={() => action(onLogout)}
              >
                <LogOut size={16} />
              </button>
            </div>
            <nav className="malvo-legal-links" aria-label={tr('Legal information', 'Rechtliche Informationen')}>
              <a href={tr('/tos','/tos?lang=de')} target="_blank" rel="noopener noreferrer">{tr('Terms of Service', 'Nutzungsbedingungen')}</a>
              <a href={tr('/legal','/legal?lang=de')} target="_blank" rel="noopener noreferrer">{tr('Legal Notice', 'Impressum')}</a>
              <a href={tr('/privacy','/privacy?lang=de')} target="_blank" rel="noopener noreferrer">{tr('Privacy', 'Datenschutz')}</a>
            </nav>
          </div>
        </aside>

        <section key={tab} className={'settings-content settings-view-' + tab}>
          {tab === 'account' && (
            <div className="flackle-account-view">
              <h3>{tr('Account Info', 'Konto-Informationen')}</h3>
              <form className="flackle-field-group" onSubmit={save}>
                <label>{tr('Username', 'Nutzername')}</label>
                <div className="flackle-field-box">
                  <AtSign size={16} className="field-prefix-icon" />
                  <input
                    value={draft.username}
                    minLength={me.username==='l'?1:3}
                    maxLength={24}
                    required
                    onChange={e =>
                      setDraft({ ...draft, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })
                    }
                    aria-label="Username"
                  />
                  {draft.username !== me.username && (
                    <button type="submit" className="flackle-inline-btn" disabled={busy}>
                      {tr('Save', 'Speichern')}
                    </button>
                  )}
                </div>
              </form>

              <div className="flackle-field-group">
                <label className="flackle-label-with-icon">
                  Email{' '}
                  <button
                    type="button"
                    title={showMail ? tr('Hide email', 'E-Mail verbergen') : tr('Show email', 'E-Mail anzeigen')}
                    onClick={() => setShowMail(!showMail)}
                  >
                    {showMail ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </label>
                {editingMail ? (
                  <form className="flackle-field-box" onSubmit={saveEmail}>
                    <Mail size={16} className="field-prefix-icon" />
                    <input
                      type="email"
                      value={mailDraft}
                      onChange={e => setMailDraft(e.target.value)}
                      required
                      autoFocus
                      placeholder={tr('New email address', 'Neue E-Mail-Adresse')}
                    />
                    <input
                      type="password"
                      value={mailPassword}
                      onChange={e => setMailPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder={tr('Current password', 'Aktuelles Passwort')}
                      aria-label={tr('Current password', 'Aktuelles Passwort')}
                    />
                    {me.twoFactorEnabled&&<input name="code" required autoComplete="one-time-code" maxLength={80} aria-label={tr('App or recovery code','App- oder Wiederherstellungscode')} placeholder={tr('App or recovery code','App- oder Wiederherstellungscode')}/>} 
                    <button
                      type="button"
                      className="flackle-inline-btn secondary-inline"
                      onClick={() => {
                        setEditingMail(false);
                        setMailDraft(me.email || '');
                      }}
                    >
                      {tr('Cancel', 'Abbrechen')}
                    </button>
                    <button type="submit" className="flackle-inline-btn" disabled={busy}>
                      {tr('Save', 'Speichern')}
                    </button>
                  </form>
                ) : (
                  <div className="flackle-field-box">
                    <span className="flackle-field-val">{showMail ? me.email || '' : '••••••••••••••'}</span>
                    <button
                      type="button"
                      className="flackle-inline-btn"
                      onClick={() => {
                        setMailDraft(me.email || '');
                        setEditingMail(true);
                      }}
                    >
                      {tr('Change Email', 'E-Mail ändern')}
                    </button>
                  </div>
                )}
              </div>

              <div className="flackle-divider" />

              <h3>{tr('Password and Security', 'Passwort & Sicherheit')}</h3>
              <div className="flackle-field-group">
                <label>{tr('Password', 'Passwort')}</label>
                <div className="flackle-field-box">
                  <Lock size={15} className="field-prefix-icon" />
                  <span className="flackle-field-val">••••••••••••</span>
                  <button
                    type="button"
                    className="flackle-inline-btn"
                    disabled={busy}
                    onClick={()=>setChangingPassword(true)}
                  >
                    {tr('Change Password', 'Passwort ändern')}
                  </button>
                </div>
              </div>

              {changingPassword&&<ChangePassword request={request} twoFactorEnabled={!!me.twoFactorEnabled} onCancel={()=>setChangingPassword(false)} onSaved={async()=>{setChangingPassword(false);setNote(tr('Password changed. Other sessions have ended.','Passwort geändert. Andere Anmeldungen wurden beendet.'));await onSaved()}}/>}
              <button type="button" className="flackle-action-row" onClick={() => setTab('security')}>
                <b>{tr('Multi-factor authentication', 'Zwei-Faktor-Authentifizierung')}</b>
                <span>
                  {me.twoFactorEnabled?tr('On','Aktiv'):tr('Off','Aus')} <ChevronRight size={16} />
                </span>
              </button>

              <button type="button" className="flackle-action-row" onClick={() => setTab('security')}>
                <b>{tr('Logged-in devices', 'Angemeldete Geräte')}</b>
                <span>
                  {sessionCount} {sessionCount === 1 ? tr('device', 'Gerät') : tr('devices', 'Geräte')}{' '}
                  <ChevronRight size={16} />
                </span>
              </button>
            </div>
          )}

          {tab === 'profile' && (
            <>
              <div className="settings-content-title">
                <h2>{tr('Edit Profile', 'Profil gestalten')}</h2>
                <span className="free-label">FREE</span>
              </div>
              <div
                className={'large-profile-preview lp-v2' + profileTheme(draft).className}
                style={profileTheme(draft).style}
              >
                <div
                  className="large-profile-banner"
                  style={{
                    backgroundImage: draft.banner ? `url('/api/files/${draft.banner}')` : undefined,
                    background: !draft.banner ? bannerFallback(draft) : undefined
                  }}
                >
                  <button type="button" className="profile-image-upload" disabled={busy} onClick={()=>setMediaPicker('banner')}><Upload size={14}/>{tr('Upload Banner / GIF','Banner / GIF ändern')}</button>
                </div>
                <label onClick={e=>{e.preventDefault();setMediaPicker('avatar');}} className="large-profile-avatar" style={{ background: avatarSrc ? 'transparent' : draft.color || '#2b2b36' }}>
                  {avatarSrc ? <img src={avatarSrc} alt={draft.displayName} /> : draft.displayName.slice(0, 2)}
                  <span>
                    <Upload size={17} />
                  </span>
                  <input
                    aria-label="Upload Avatar"
                    disabled={busy}
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) setCropPending({ file: f, kind: 'avatar' });
                      e.target.value = '';
                    }}
                  />
                </label>
                <StatusBubble person={me} text={statusDraft}/>
                <div className="lp-identity">
                  <b>
                    <ProfileSecretTrigger person={draft} viewerStatus={me.status}>{draft.displayName || draft.username}</ProfileSecretTrigger>
                    <IdentityBadges person={{ ...me, guildTags: [] }} />
                  </b>
                  <span>@{draft.username}</span>
                  {previewBadges.length > 0 && (
                    <div className="profile-badge-icons">
                      {previewBadges.map(b => (
                        <BadgeToken key={b.id} badge={b} lang={lang} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="lp-sections">
                  <section>
                    <h4>{tr('About me', 'Über mich')}</h4>
                    <p className={draft.bio.trim() ? '' : 'is-empty'}>
                      {draft.bio.trim() ? <ProfileBio text={draft.bio.trim()} /> : tr('Tell people a little about yourself…', 'Erzähl etwas über dich…')}
                    </p>
                  </section>
                  <section>
                    <h4><ProfileSecretTrigger person={draft} viewerStatus={me.status}>{tr('Member since', 'Mitglied seit')}</ProfileSecretTrigger></h4>
                    <p>{getAccountAgeText(draft.created, lang)}</p>
                  </section>
                </div>
              </div>

              <div className="settings-profile-fields">
              <form className="settings-edit-form" onSubmit={save}>
                <fieldset disabled={busy}>
                  <div className="settings-two-col">
                    <label>
                      {tr('Display Name', 'Anzeigename')}
                      <input
                        value={draft.displayName}
                        maxLength={32}
                        required
                        onChange={e => setDraft({ ...draft, displayName: e.target.value })}
                      />
                    </label>
                    <label>
                      {tr('Username (@handle)', 'Nutzername (@username)')}
                      <input
                        value={draft.username}
                        minLength={me.username==='l'?1:3}
                        maxLength={24}
                        required
                        onChange={e =>
                          setDraft({ ...draft, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })
                        }
                      />
                      {Number((me as any).usernameChangedAt || 0) > Date.now() - 86400000 && <small className="username-cooldown">{tr('You can change your username again on ', 'Du kannst deinen Nutzernamen wieder ändern ab ')}{new Date(Number((me as any).usernameChangedAt) + 86400000).toLocaleString(lang === 'de' ? 'de-DE' : 'en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</small>}
                    </label>
                  </div>
                  <label>
                    {tr('About Me / Bio', 'Über mich')}
                    <textarea
                      value={draft.bio}
                      maxLength={300}
                      rows={3}
                      onChange={e => setDraft({ ...draft, bio: e.target.value })}
                    />
                    <small>{draft.bio.length}/300</small>
                  </label>
                  <GuildTagPicker request={request} onSaved={onSaved} />

                  <div className="profile-gradient-controls">
                    <GradientPicker c1={draft.color || '#ef5252'} c2={draft.color2 || '#262636'} enabled={!!draft.profileGradient} onChange={(a, b, on) => setDraft({ ...draft, color: a, color2: b, profileGradient: on })} />

                    {me.staffBadgeUnlocked && (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!draft.hideStaffBadge}
                        className={'animated-perm-toggle ' + (!draft.hideStaffBadge ? 'is-checked' : '')}
                        style={{ marginTop: '12px' }}
                        onClick={() => setDraft({ ...draft, hideStaffBadge: !draft.hideStaffBadge })}
                      >
                        <span className="animated-perm-box">
                          <Check size={13} strokeWidth={3} />
                        </span>
                        <span className="animated-perm-label">{tr('Show MALVO Team badge', 'MALVO-Team-Abzeichen anzeigen')}</span>
                      </button>
                    )}

                    {me.moderatorBadgeUnlocked && (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!draft.hideModeratorBadge}
                        className={'animated-perm-toggle ' + (!draft.hideModeratorBadge ? 'is-checked' : '')}
                        style={{ marginTop: '12px' }}
                        onClick={() => setDraft({ ...draft, hideModeratorBadge: !draft.hideModeratorBadge })}
                      >
                        <span className="animated-perm-box">
                          <Check size={13} strokeWidth={3} />
                        </span>
                        <span className="animated-perm-label">{tr('Show MALVO Moderator badge', 'MALVO-Moderator-Abzeichen anzeigen')}</span>
                      </button>
                    )}

                    {me.developerBadgeUnlocked && (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!draft.hideDeveloperBadge}
                        className={'animated-perm-toggle ' + (!draft.hideDeveloperBadge ? 'is-checked' : '')}
                        style={{ marginTop: '12px' }}
                        onClick={() => setDraft({ ...draft, hideDeveloperBadge: !draft.hideDeveloperBadge })}
                      >
                        <span className="animated-perm-box">
                          <Check size={13} strokeWidth={3} />
                        </span>
                        <span className="animated-perm-label">{tr('Show MALVO Developer badge', 'MALVO-Developer-Abzeichen anzeigen')}</span>
                      </button>
                    )}

                  </div>

                  <div className="profile-badges-selector">
                    <label className="badges-section-label">
                      <Award size={15} /> {tr('Profile Badges (Select up to 6)', 'Profil-Badges (Wähle bis zu 6)')}
                    </label>
                    <div className="badges-picker-grid icon-only" onMouseLeave={() => setBadgeHint('')}>
                      {AVAILABLE_CUSTOM_BADGES.map(b => {
                        const active = (draft.customBadges || []).includes(b.id);
                        const expiredEarly = b.id === "early" && !me.earlySupporterUnlocked && !(me.customBadges || []).includes("early") && Date.now() >= (me.earlySupporterCutoff || Date.parse("2026-11-04T00:00:00+01:00"));
                        const name = lang === 'de' ? b.labelDe : b.label;
                        return (
                          <button
                            key={b.id}
                            type="button"
                            className={'badge-pick-tile' + (active ? ' is-selected' : '')}
                            style={{ '--badge-color': b.color } as React.CSSProperties}
                            aria-label={name}
                            aria-pressed={active}
                            disabled={expiredEarly}
                            title={expiredEarly ? tr('Early Supporter is no longer available', 'Early Supporter kann nicht mehr freigeschaltet werden') : name}
                            onMouseEnter={() => setBadgeHint(name)}
                            onFocus={() => setBadgeHint(name)}
                            onClick={() => toggleBadge(b.id)}
                          >
                            <BadgeIcon id={b.id} size={26} />
                            <span className="badge-pick-check"><Check size={11} strokeWidth={3.4} /></span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="badge-pick-hint" aria-live="polite">
                      {badgeHint || `${(draft.customBadges || []).length}/6 ${tr('selected', 'ausgewählt')}`}
                    </p>
                  </div>

                  <div className="settings-custom-select-field">
                    <span className="field-label-text">{tr('Online Status', 'Online-Status')}</span>
                    <CustomSelect
                      value={draft.status}
                      onChange={val => setDraft({ ...draft, status: val })}
                      options={[
                        { value: 'online', label: tr('Online', 'Online'), dotColor: '#3ba55c' },
                        { value: 'idle', label: tr('Idle / Away', 'Abwesend'), dotColor: '#faa61a' },
                        {
                          value: 'dnd',
                          label: tr('Do Not Disturb (Mutes Notifications & Calls)', 'Bitte nicht stören (Keine Töne)'),
                          dotColor: '#ed4245'
                        },
                        { value: 'invisible', label: tr('Invisible', 'Unsichtbar'), dotColor: '#747f8d' }
                      ]}
                    />
                  </div>

                  {(profileDirty || busy) && (
                    <div className="profile-save-bar" role="status">
                      <span>{tr('Careful — you have unsaved changes!', 'Achtung – du hast ungespeicherte Änderungen!')}</span>
                      <button type="button" className="profile-save-reset" onClick={resetProfileDraft}>
                        {tr('Reset', 'Zurücksetzen')}
                      </button>
                      <button className="primary">
                        {busy ? tr('Saving…', 'Speichern …') : tr('Save changes', 'Änderungen speichern')}
                      </button>
                    </div>
                  )}
                </fieldset>
              </form>
              <CustomStatusEditor person={me} request={request} onSaved={onSaved} onDraft={setStatusDraft}/>
              </div>
            </>
          )}

          {tab === 'appearance' && (
            <div className="appearance-customizer">
              <label className="platform-switch"><input type="checkbox" checked={showDiscovery} onChange={e=>{setShowDiscovery(e.target.checked);localStorage.setItem('malvo-show-discovery',e.target.checked?'1':'0');window.dispatchEvent(new Event('malvo-settings-updated'))}}/>„Server finden“ in der Serverleiste anzeigen</label>
              <h2>{tr('Appearance & Language', 'Design & Sprache')}</h2>
              <p>
                {tr(
                  'Customize the look, colors, button style, and language of MALVO Chat.',
                  'Passe das Aussehen, die Farben, Buttons und die Sprache von MALVO Chat an.'
                )}
              </p>

              <div className="customizer-block">
                <h4>
                  <Globe size={16} /> {tr('Language / Sprache', 'Sprache / Language')}
                </h4>
                <div className="customizer-pills">
                  <button
                    type="button"
                    className={'customizer-pill ' + (lang === 'en' ? 'active' : '')}
                    onClick={() => setLang('en')}
                  >
                    🇬🇧 English (Default)
                  </button>
                  <button
                    type="button"
                    className={'customizer-pill ' + (lang === 'de' ? 'active' : '')}
                    onClick={() => setLang('de')}
                  >
                    🇩🇪 Deutsch
                  </button>
                </div>
              </div>

              <div className="customizer-block">
                <h4>
                  <Paintbrush size={16} /> {tr('Theme Preset', 'Farbschema / Theme')}
                </h4>
                <div className="theme-presets-grid">
                  {Object.entries(THEME_PRESETS).map(([key, p]) => (
                    <button
                      key={key}
                      type="button"
                      className={'theme-preset-card ' + (themePreset === key ? 'active' : '')}
                      onClick={() => {
                        setThemePreset(key);
                        setAccentColor(p.accent);
                        localStorage.setItem('malvo-theme', key);
                        localStorage.setItem('malvo-accent', p.accent);
                        applyAppearanceSettings();
                      }}
                    >
                      <span
                        className="theme-preset-swatch"
                        style={{ background: `linear-gradient(135deg, ${p.bgMain} 55%, ${p.accent} 100%)` }}
                      />
                      <b>{p.label}</b>
                      {themePreset === key && <Check size={14} />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="customizer-block">
                <h4>{tr('Accent Color', 'Akzentfarbe')}</h4>
                <div className="role-swatches">
                  {['#ef5252', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4'].map(c => (
                    <button
                      key={c}
                      type="button"
                      className={'role-swatch ' + (accentColor.toLowerCase() === c ? 'selected' : '')}
                      style={{ background: c }}
                      onClick={() => {
                        setAccentColor(c);
                        localStorage.setItem('malvo-accent', c);
                        applyAppearanceSettings();
                      }}
                    >
                      {accentColor.toLowerCase() === c && <Check size={13} strokeWidth={3} color="#fff" />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="customizer-block">
                <h4>{tr('Button Shape & Corner Style', 'Button-Form')}</h4>
                <div className="customizer-pills">
                  {[
                    ['rounded', tr('Modern Rounded', 'Modern Abgerundet')],
                    ['pill', tr('Smooth Pill', 'Runde Pill-Form')],
                    ['sharp', tr('Clean Crisp', 'Kantig Clean')]
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      className={'customizer-pill ' + (btnStyle === k ? 'active' : '')}
                      onClick={() => {
                        setBtnStyle(k);
                        localStorage.setItem('malvo-btn-style', k);
                        applyAppearanceSettings();
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="customizer-block">
                <h4>{tr('Chat Message Spacing', 'Nachrichtenabstand')}</h4>
                <div className="customizer-pills">
                  {[
                    ['normal', tr('Comfortable', 'Standard (Komfortabel)')],
                    ['compact', tr('Compact', 'Kompakt')]
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      className={'customizer-pill ' + (density === k ? 'active' : '')}
                      onClick={() => {
                        setDensity(k);
                        localStorage.setItem('malvo-density', k);
                        applyAppearanceSettings();
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {['account','security'].includes(tab) && (
            <>
              <h2>{tr('Security & Devices', 'Sicherheit & Geräte')}</h2>
              <TwoFactorSettings request={request} onChanged={onSaved}/>

              <div className="flackle-divider" />
              <AccountDeletion request={request} twoFactor={!!me.twoFactorEnabled}/>
            </>
          )}

          {tab === 'privacy' && (
            <>
              <h2>{tr('Privacy & Safety', 'Privatsphäre & Sicherheit')}</h2>
              <p>
                {tr(
                  'Manage blocked contacts and your privacy preferences on MALVO Chat.',
                  'Verwalte blockierte Kontakte und deine Sicherheitseinstellungen auf MALVO Chat.'
                )}
              </p>
              <button className="settings-row" onClick={onBlocked}>
                <Shield size={16} />
                {tr('Manage Blocked Users', 'Blockierte Kontakte verwalten')} <ChevronRight size={16} />
              </button>
            </>
          )}

          {tab === 'beta' && (
            <>
              <h2>Closed Beta</h2>
              <p>
                {tr(
                  'You are running the latest MALVO Chat release with a custom media player, call games, and Windows Desktop App.',
                  'Du nutzt die aktuelle MALVO Chat Edition mit eigenem Videoplayer, Minispielen und Windows-Desktop-App.'
                )}
              </p>
              <div className="desktop-pwa-card">
                <b>{tr('Build Status: Active', 'Build-Status: Aktiv')}</b>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#9898a8' }}>
                  {tr(
                    'All features (uploads up to 5 GB, voice & screen sharing, Chess/Tic-Tac-Toe games, verified Malvo server) are unlocked for your account.',
                    'Alle Funktionen (Datei-Uploads bis 5 GB, Sprachräume, Schach & Minispiele, Verifizierter Malvo-Server) sind freigeschaltet.'
                  )}
                </p>
              </div>
            </>
          )}

          {tab === 'chat' && (
            <>
              <h2>{tr('Chat Settings', 'Chat-Einstellungen')}</h2>
              <p>
                {tr(
                  'Customize how messages and media are displayed in chat.',
                  'Passe das Verhalten beim Schreiben und Lesen von Nachrichten an.'
                )}
              </p>
              <div className="settings-info-field">
                <label>{tr('Message Spacing', 'Nachrichtenabstand')}</label>
                <CustomSelect
                  value={density}
                  onChange={val => {
                    setDensity(val);
                    localStorage.setItem('malvo-density', val);
                    applyAppearanceSettings();
                  }}
                  options={[
                    { value: 'normal', label: tr('Standard (Comfortable)', 'Standard (Komfortabel)') },
                    { value: 'compact', label: tr('Compact', 'Kompakt') }
                  ]}
                />
              </div>
              <div className="settings-info-field">
                <label>{tr('When opening a chat','Beim Öffnen eines Chats')}</label>
                <CustomSelect value={chatPosition} onChange={value=>{setChatPosition(value);localStorage.setItem('malvo-chat-open-mode',value)}} options={[
                  {value:'bottom',label:tr('Newest messages (default)','Neueste Nachrichten (Standard)')},
                  {value:'last-position',label:tr('Last reading position','Letzte Leseposition')}
                ]}/>
                <p>{tr('Reading positions are saved on this device for each account and chat. If the saved message is outside the loaded history, the chat opens at the newest messages.','Lesepositionen werden auf diesem Gerät pro Account und Chat gespeichert. Liegt die gespeicherte Nachricht außerhalb des geladenen Verlaufs, öffnet sich der Chat bei den neuesten Nachrichten.')}</p>
              </div>
              <div className="density-preview">
                <b>{tr('Message Preview', 'Nachrichtenvorschau')}</b>
                <p>
                  {tr(
                    'Enter sends a message · Shift + Enter creates a new line · Hold Shift while hovering a message to quick-delete.',
                    'Enter sendet eine Nachricht · Shift + Enter erzeugt einen Zeilenumbruch · Halte Shift gedrückt für Schnell-Löschen.'
                  )}
                </p>
              </div>
            </>
          )}

          {tab === 'keybinds' && <KeybindSettings />}

          {tab === 'notifications' && (
            <>
              <h2>{tr('Notifications', 'Benachrichtigungen')}</h2>
              {/MALVOAndroidNotifications\/1/.test(navigator.userAgent)&&<p><a href="/android/notifications">{tr('Android notification settings','Android-Benachrichtigungen einrichten')}</a><br/><small>{tr('Allow Android notifications to receive messages and calls in the background.','Erlaube Android-Benachrichtigungen, damit Nachrichten und Anrufe auch im Hintergrund ankommen.')}</small></p>}
              <p>
                {tr(
                  'Control sound effects for incoming calls, messages, and voice rooms. Tip: Setting your status to Do Not Disturb automatically mutes all notification sounds.',
                  'Steuere Töne für eingehende Anrufe und Nachrichten. Tipp: Der Status „Bitte nicht stören“ schaltet alle Hinweistöne automatisch stumm.'
                )}
              </p>
              <button type="button" style={{ marginTop: '12px' }} className={'animated-perm-toggle '+(outgoingRing?'is-checked':'')} role="switch" aria-checked={outgoingRing} onClick={()=>{const next=!outgoingRing;setOutgoingRing(next);localStorage.setItem('malvo-outgoing-ring',next?'1':'0');}}><span className="animated-perm-box"><Check size={13}/></span><span className="animated-perm-label">{tr('Play ringtone while calling someone','Klingelton beim Anrufen abspielen')}</span></button>
              <button
                type="button"
                className={'animated-perm-toggle ' + (notifSound ? 'is-checked' : '')}
                style={{ marginTop: '18px' }}
                onClick={() => {
                  const next = !notifSound;
                  setNotifSound(next);
                  localStorage.setItem('malvo-notif-sound', next ? '1' : '0');
                }}
              >
                <span className="animated-perm-box">
                  <Check size={13} strokeWidth={3} />
                </span>
                <span className="animated-perm-label">
                  {tr('Enable call, join & message sound effects', 'Anruf-, Beitritts- und Nachrichtentöne aktivieren')}
                </span>
              </button>
              {([
                [notifPopups,setNotifPopups,'malvo-notif-popups',tr('Show message popups in MALVO','Nachrichten-Pop-ups in MALVO anzeigen')],
                [notifDesktop, setNotifDesktop, 'malvo-notif-desktop', tr('Show desktop notifications (DMs and @mentions)', 'Desktop-Benachrichtigungen anzeigen (DMs und @Erwähnungen)')],
                [notifAllServer, setNotifAllServer, 'malvo-notif-all-server', tr('Also notify for every server message', 'Auch bei jeder Server-Nachricht benachrichtigen')],
                [shareAct, setShareAct, 'malvo-share-activity', tr('Show game and music activity on my profile', 'Spiel- und Musikaktivität in meinem Profil anzeigen')]
              ] as [boolean, (v: boolean) => void, string, string][]).map(([value, set, key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={'animated-perm-toggle ' + (value ? 'is-checked' : '')}
                  style={{ marginTop: '12px' }}
                  onClick={() => { const next = !value; set(next); localStorage.setItem(key, next ? '1' : '0'); if (key === 'malvo-share-activity') window.dispatchEvent(new Event('malvo-activity-share')); }}
                >
                  <span className="animated-perm-box"><Check size={13} strokeWidth={3} /></span>
                  <span className="animated-perm-label">{label}</span>
                </button>
              ))}

            </>
          )}

          {tab === 'desktop' && (
            <>
              <section className="android-app-settings"><h2>MALVO für Android</h2><p>{tr('Early Beta for Android 8 or later. App updates are checked automatically; Android asks you to confirm installation.','Early Beta ab Android 8. App-Updates werden automatisch geprüft; die Installation bestätigst du in Android.')}</p>
                {/MALVOAppUpdater\/1/.test(navigator.userAgent)?<a className="primary android-update-check" href="/android/check-update"><Download size={18}/>{tr('Check app updates','App-Updates prüfen')}</a>:<a className="primary" href="/download/android" download><Download size={18}/>{tr('Download Android APK','Android-APK herunterladen')}</a>}
                <small>{tr('The first debug beta may need to be reinstalled once when switching to the permanent update signature.','Beim Wechsel von der ersten Debug-Beta auf die feste Update-Signatur kann einmalig eine Neuinstallation nötig sein.')}</small>
              </section><details className="other-platform-downloads" open={!/Android/i.test(navigator.userAgent)}><summary>{tr('Windows, macOS & Linux downloads','Downloads für Windows, macOS & Linux')}</summary><h2>MALVO Desktop App</h2><MacDownloads/>
              <p>
                {tr(
                  'Experience MALVO Chat as a standalone Windows app (.exe) with custom frameless titlebar and native screen sharing.',
                  'Erlebe MALVO Chat als eigenständige Windows-Anwendung (.exe) mit eigener Titelleiste und nativer Bildschirmfreigabe.'
                )}
              </p>
              <div className="desktop-download-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <img
                    src="/icon.png"
                    style={{ width: '48px', height: '48px', borderRadius: '12px' }}
                    alt="MALVO Icon"
                  />
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px' }}>MALVO Chat for Windows (x64)</h3>
                    <small style={{ color: '#9696a4' }}>
                      Version 1.4.0 · {tr('Official Windows Application (.exe)', 'Offizielle Windows-Anwendung (.exe)')}
                    </small>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '13.5px', color: '#b8b8c4', lineHeight: '1.5' }}>
                  {tr(
                    'Includes frameless titlebar with built-in Update button, system tray, native screen sharing with audio, and automatic headset/webcam access.',
                    'Mit rahmenloser Titelleiste inkl. Update-Button, System-Tray, Bildschirmübertragung mit Ton und automatischer Headset-/Webcam-Freigabe.'
                  )}
                </p>
                <a
                  href="/downloads/MALVO-Chat-Setup.exe"
                  download="MALVO-Chat-Setup.exe"
                  className="primary"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    textDecoration: 'none',
                    padding: '10px 18px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#fff'
                  }}
                >
                  <Download size={16} /> {tr('Download for Windows (.exe)', 'Windows App herunterladen (.exe)')}
                </a>
              </div></details>
            </>
          )}

          {tab === 'voice' && <AudioSettings />}
          {tab === 'clips' && <ClipsSettings/>}
          {tab === 'support' && me.isOwnerAdmin && <SupportAdmin request={request} />}
          {tab==='sessions'&&<AccountSessions request={request}/>}
          {tab==='connections'&&<><h2>{tr('Connections','Verbindungen')}</h2><p className="settings-section-intro">{tr('Link your accounts to show them on your profile. When you are live on YouTube or Twitch, others see it right away.','Verknüpfe deine Konten, damit sie in deinem Profil erscheinen. Wenn du auf YouTube oder Twitch live bist, sehen andere das sofort.')}</p><SpotifyConnect/><LinkConnections/></>}
          {tab==='info'&&<AppInformation/>}
          {tab === 'data' && <PersonalData request={request} />}
          {tab === 'subscription' && <Subscription request={request} />}
          {mediaPicker&&<ProfileMediaPicker closing={mediaPickerClosing} busy={busy} onClose={()=>setMediaPicker(null)} onFile={file=>{setCropPending({file,kind:mediaPicker});setMediaPicker(null);}} onGif={async url=>{setBusy(true);try{const result=await request('/profile/media/import','POST',{url,kind:mediaPicker,stageOnly:true});const response=await fetch('/api/files/'+encodeURIComponent(result.id));if(!response.ok)throw Error(tr('Could not load GIF','GIF konnte nicht geladen werden'));const blob=await response.blob();setCropPending({file:new File([blob],'catalogue.gif',{type:blob.type}),kind:mediaPicker});setMediaPicker(null);}finally{setBusy(false);}}}/>}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {note && (
            <p className="form-success" role="status">
              <Check size={14} />
              {note}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
